/* eslint-disable @typescript-eslint/no-unsafe-return */
import React, { useRef, useEffect } from 'react'
import instrument from '@adc/instrument'
import classnames from 'classnames/bind'
import {
  useApplicationContext,
  Button,
  Icon,
  Text,
  Grid,
  InfoPill,
  Chip,
  constants,
  hooks,
} from '@adc/ui-adc'
import type { AiChatMode } from '@adc/ui-adc'
import type { CombinedListingType } from '../../context/listing'
import SectionPanel from '../../components/sectionPanel'
import analytics from '../../../common/services/analytics'
import { getFAQS } from './faqs'
import styles from './index.scss'
const cx = classnames.bind(styles)

const { ColorNames } = constants
const OPEN_ENDED_QUESTION = 'Ask Something Else'

/**
 * FAQs with AI...
 * This component displays a set of FAQ buttons that when clicked will trigger the AI
 * chat with a pre-defined question. The purpose is to gauge user interest in an AI
 * assistant feature before fully developing it.
 *
 * As of DEC 2025, we are no longer using "mocK" mode (fake door) and have moved to "chat" mode
 */
export const getAiFaqMode = (featureFlags: {
  [key: string]: unknown
}): Extract<AiChatMode, 'chat'> | undefined => {
  const aiAssistantFlag = featureFlags['fe.resi.ai-assistant'] as
    | { enabled?: boolean; faqMode?: Extract<AiChatMode, 'chat'> }
    | undefined
  return aiAssistantFlag?.enabled === true
    ? aiAssistantFlag?.faqMode
    : undefined
}

interface FaqsWithAiProps {
  listing?: CombinedListingType
  onClickAiPrompt: (prompt?: string) => void
}

export const FaqsWithAi = ({ listing, onClickAiPrompt }: FaqsWithAiProps) => {
  const { featureFlags } = useApplicationContext()
  const faqMode = getAiFaqMode(featureFlags) // 'chat' | undefined
  const hasScrolledToFAQs = useRef(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const context = `web.pdp.faqsWithAi.${faqMode}`
  const isMobile = hooks.useIsMobileHook()

  useEffect(() => {
    instrument.increment(context, 1, ['action:load'])

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasScrolledToFAQs.current) {
            hasScrolledToFAQs.current = true
            instrument.increment(context, 1, ['action:view'])
          }
        })
      },
      {
        rootMargin: '0px 0px -100px 0px',
        threshold: 0,
      }
    )

    const currentSectionRef = sectionRef.current
    if (currentSectionRef) observer.observe(currentSectionRef)

    return () => {
      if (currentSectionRef) observer.unobserve(currentSectionRef)
    }
  }, [context, faqMode])

  if (!faqMode) return null

  const faqs: ReturnType<typeof getFAQS> = listing ? getFAQS(listing) : []
  const noFaqsDescription = 'No FAQs available for this listing at this time.'

  /**
   * Button click handler for FAQ buttons.
   */
  const handleClick = (
    index: number,
    faq: ReturnType<typeof getFAQS>[number]
  ) => {
    // mode comes strictly from feature flag configuration
    instrument.increment(context, 1, [
      'action:click',
      `button:faq_${index + 1}`,
      `button:name_${faq.shortName}`,
    ])
    onClickAiPrompt(faq.question)

    analytics.aiAssistantChat.trackAiQuestionClick(index + 1, faq.shortName)
  }

  return (
    <SectionPanel elmId='ai_faqs' compact='top-only' collapsible={false}>
      <Grid>
        <Grid.Column computer={10} tablet={8} mobile={16}>
          <div className={cx('header')} ref={sectionRef}>
            <Icon
              name='sparkles'
              size='large'
              color='adcLightBlue'
              iconStyle='regular'
              fitted
            />
            <Text.Header
              level={isMobile ? 3 : 2}
              style={{ marginBottom: 0 }}
              color={ColorNames.GrayDark}
              // @ts-ignore next
              weight='medium'
            >
              Ask Auction.com
            </Text.Header>
            <InfoPill
              foregroundColor={ColorNames.GrayMedium}
              backgroundColor={ColorNames.White}
              content='AI Beta'
              includeBorder
              size='x-small'
              style={{
                marginLeft: '9px',
                marginTop: '4px',
              }}
            />
          </div>
          {!faqs ? (
            <div className={cx('no-faqs')}>{noFaqsDescription}</div>
          ) : (
            <>
              <div className={cx('button-container')}>
                {faqs.map((faq, index) => (
                  <Chip
                    key={faq.question}
                    className={cx('faq-button')}
                    fluid
                    onClick={() => handleClick(index, faq)}
                    role='secondary'
                    // @ts-ignore next
                    color={ColorNames.Info}
                    elmId={`faq-button-${index + 1}`}
                    content={faq.question}
                  />
                ))}
              </div>
              <div>
                <Button
                  size='small'
                  key={OPEN_ENDED_QUESTION}
                  className={cx('faq-button', 'faq-open-question-button')}
                  onClick={() =>
                    handleClick(faqs.length, {
                      question: undefined,
                      shortName: 'else',
                    })
                  }
                  role='primary'
                  // @ts-ignore next
                  color={ColorNames.Info}
                  elmId={`faq-button-${faqs.length + 1}`}
                >
                  {OPEN_ENDED_QUESTION}
                  <Icon
                    className={cx('faq-open-question-icon')}
                    size='medium'
                    iconStyle='solid'
                    name='comment-arrow-up-right'
                    color={ColorNames.White}
                  />
                </Button>
              </div>
            </>
          )}
        </Grid.Column>
      </Grid>
    </SectionPanel>
  )
}

export default FaqsWithAi
